import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getBaseUrl } from './ApiContext';
import './NetworkChat.css';

const baseUrl = getBaseUrl();

// Ask the whole team's network a question in your own words.
//
// The answers come from the same lookups the introductions tooling uses,
// running in kyber-outreach; this page only asks and renders. Two things
// about it are deliberate rather than incidental:
//
//   1. Every answer shows the lookups that produced it. A model choosing
//      the wrong lookup writes an answer just as fluent as a right one,
//      and the query line is how a reader catches that. It is not debug
//      output and should not be hidden behind a toggle.
//   2. A list is UPLOADED, not pasted into the question. Three hundred
//      company names in a chat box is unreadable, and the names go to the
//      lookup directly rather than through the model, which cannot then
//      drop or "correct" any of them.
const EXAMPLES = [
    'Do we know anyone at Blackstone?',
    'Who do we know at CalPERS, Sequoia or Insight Partners?',
    'Who do we know who runs ops at a hospital system?',
    'Do we know Kenny Glennon?',
];

// Links in an answer open in a new tab. Most are the LinkedIn profile on
// each person's name (2026-09-11), and following one in place would leave
// the conversation mid-read.
const markdownComponents = {
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
    ),
};

// What the answer actually looked at, in English. This line is the only
// way a reader can tell a wrong lookup from a right one, since both produce
// equally fluent prose -- but the readers are investors, not engineers, so
// it says "Searched our contacts at Blackstone", never
// paths_to_company("Blackstone").
// Who holds the relationship decides what the button says. Most paths are
// somebody else's contact, so naming them is the whole point: "Draft for
// Sunny to forward" is the true action, and a button that just said "draft
// an intro" would hide that the reader cannot send it. Writing to a
// stranger who has never met you is the defect forward_request.py exists
// to prevent, and a button is a bad place to bury it.
//
// The signed-in person becomes "me" rather than their own first name,
// which is how Alexandra asked for it on 2026-09-09: reading your own name
// back at you in a list of five is a small stumble every single time.
function nameFor(owner, me) {
    if (!owner) return null;
    if (me && owner.toLowerCase() === me.toLowerCase()) return 'me';
    return owner.charAt(0).toUpperCase() + owner.slice(1);
}

function actionFor(person, me) {
    const owners = person.known_by || [];
    // If it is yours as well as somebody else's, it is yours: you can act
    // without asking a colleague for anything.
    const mine = me && owners.some((o) => o.toLowerCase() === me.toLowerCase());
    const owner = mine ? me : owners[0];
    const who = nameFor(owner, me);
    const at = person.firm ? ` at ${person.firm}` : '';

    if (!who) {
        return {
            label: `Draft an intro to ${person.name}`,
            prompt: `Draft the introduction email to ${person.name}${at}, `
                + 'for the portfolio company we have been discussing.',
        };
    }
    return {
        label: `Draft for ${who} to forward to ${person.name}`,
        prompt: `Draft the email for ${who === 'me' ? me : who} to forward to `
            + `${person.name}${at}, for the portfolio company we have been `
            + 'discussing.',
    };
}

function describeQuery(q) {
    const a = q.args || {};
    switch (q.tool) {
        case 'paths_to_company':
            return `Searched our contacts at ${a.company}`;
        case 'paths_to_company_list':
        case 'paths_to_attached_list':
            return `Searched our contacts at ${a.count} companies`;
        case 'who_do_we_know':
            return `Looked up ${a.name}`;
        case 'contacts_matching_profile':
            return `Searched our contacts for ${a.profile}`;
        case 'correspondence_with':
            return `Checked email history with ${(a.names || []).join(', ')}`;
        case 'investors_in':
            return `Looked up who backed ${a.company}`;
        case 'draft_intro':
            return `Drafted an introduction to ${a.target}`;
        case 'perplexity_search':
            return `Searched the web: ${a.question}`;
        default:
            return q.tool;
    }
}

function NetworkChat() {
    const [turns, setTurns] = useState([]);
    const [input, setInput] = useState('');
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    // The conversation this thread belongs to. Assigned by the server on
    // the first answer, so a chat only exists once it has one.
    const [chatId, setChatId] = useState(null);
    const [saved, setSaved] = useState([]);
    // Whose contacts count as "mine" when labelling an action. Comes back
    // with each answer from the verified session rather than being guessed
    // in the browser.
    const [firstName, setFirstName] = useState(null);
    const [error, setError] = useState('');
    const endRef = useRef(null);
    const fileRef = useRef(null);
    const boxRef = useRef(null);
    // Held so the composer's stop button can abort the request in flight.
    const abortRef = useRef(null);

    useEffect(() => {
        // Optional-called: jsdom has no scrollIntoView, and a missing
        // scroll must never be the thing that stops the page rendering.
        endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
    }, [turns, busy]);

    // Return makes a NEW LINE and does not send (Afnaan, 2026-09-17). A
    // question to this tool is routinely three lines long, and losing one
    // half-written to a stray return is the whole reason he asked twice.
    // Sending is the arrow, or cmd/ctrl + return for anyone who wants a
    // keystroke, which is also why this is a textarea rather than an input:
    // an input submits its form on return and there is no way to stop it
    // without also stopping the newline.
    const onComposerKey = (e) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            send();
        }
    };

    // A textarea keeps one row's height whatever is in it, so the box has to
    // be grown by hand or everything past the first line is typed out of
    // sight. Capped, then it scrolls: a pasted page must not push the
    // conversation off the screen.
    useEffect(() => {
        const box = boxRef.current;
        if (!box) return;
        box.style.height = 'auto';
        box.style.height = `${Math.min(box.scrollHeight, 180)}px`;
    }, [input]);

    const loadHistory = useCallback(() => {
        axios.get(`${baseUrl}/api/network-chat/history`)
            // A history that will not load must not take the page with it:
            // you can still ask a question with no sidebar.
            .then((r) => setSaved(r.data?.chats || []))
            .catch(() => setSaved([]));
    }, []);

    useEffect(loadHistory, [loadHistory]);

    const openChat = async (id) => {
        if (busy) return;
        try {
            const { data } = await axios.get(
                `${baseUrl}/api/network-chat/history/${id}`);
            setTurns((data.messages || []).map((m) => ({
                role: m.role, text: m.text, queries: m.queries,
            })));
            setChatId(id);
        } catch {
            setError('That conversation could not be opened.');
        }
    };

    // Under streaming this stops the MODEL, not just the waiting, and that
    // changed when the stream landed. Aborting closes the connection; the
    // proxy's generator breaks on its next yield, which closes the upstream
    // one, which exits the `with client.beta.messages.stream(...)` block in
    // ask_stream and cancels the request mid-answer.
    //
    // The one thing it cannot interrupt is a database lookup already in
    // flight: those run to completion between yields, so a search that has
    // started will finish before the loop notices nobody is listening. In
    // practice that is under a second now the mail archive is indexed.
    const stop = () => {
        abortRef.current?.abort();
    };

    // Stopping has to LOOK like it stopped. Without this the answer simply
    // vanished and the question reappeared in the box, which reads as the
    // page having lost it rather than as the stop working (Alexandra,
    // 2026-09-17: "i literally press the button and it just runs the same
    // prompt again no stop").
    const [stopped, setStopped] = useState(false);

    // Placing is a separate, explicit act. The model composes; a person
    // decides it goes in a mailbox. Keeping those apart means no sequence
    // of words in a question can cause a write.
    // What the answer is doing right now, in the same plain words the
    // evidence line uses. This is the half of streaming that matters: the
    // long silence is the lookups, not the writing.
    const [steps, setSteps] = useState([]);
    const [streamed, setStreamed] = useState('');
    const [placing, setPlacing] = useState(null);
    const [placed, setPlaced] = useState({});

    const placeDraft = async (turnIndex, draft) => {
        if (placing !== null) return;
        setPlacing(turnIndex);
        setError('');
        try {
            const { data } = await axios.post(
                `${baseUrl}/api/network-chat/place`, draft);
            setPlaced((p) => ({ ...p, [turnIndex]: data.placed_in || true }));
        } catch (e) {
            setError(e.response?.data?.error
                || 'The draft could not be saved to your Drafts.');
        } finally {
            setPlacing(null);
        }
    };

    const newChat = () => {
        setTurns([]);
        setChatId(null);
        setError('');
    };

    const removeChat = async (id, e) => {
        e.stopPropagation();
        try {
            await axios.delete(`${baseUrl}/api/network-chat/history/${id}`);
            if (id === chatId) newChat();
            loadHistory();
        } catch {
            setError('That conversation could not be deleted.');
        }
    };

    const send = async (text) => {
        const question = (text ?? input).trim();
        if (!question || busy) return;
        setError('');
        setStopped(false);
        setInput('');
        setBusy(true);

        // Only the prose goes into history. Tool results are re-fetched by
        // the next question if it needs them, so a stale contact list can
        // never be quoted back as current.
        const history = turns.map((t) => ({ role: t.role, content: t.text }));
        const asked = { role: 'user', text: question, file: file?.name };
        setTurns((prev) => [...prev, asked]);

        let csv = null;
        if (file) {
            try {
                csv = await file.text();
            } catch {
                setError('That file could not be read.');
                setBusy(false);
                return;
            }
        }

        const controller = new AbortController();
        abortRef.current = controller;
        setSteps([]);
        setStreamed('');
        try {
            // fetch rather than axios: this reads a body as it arrives, and
            // axios buffers the whole response before resolving.
            const token = axios.defaults.headers.common.Authorization;
            const resp = await fetch(`${baseUrl}/api/network-chat/stream`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: token } : {}),
                },
                body: JSON.stringify({ question, history, csv, chat_id: chatId }),
                signal: controller.signal,
            });
            if (!resp.ok) {
                let detail = '';
                try { detail = (await resp.json()).error; } catch { /* no body */ }
                throw new Error(detail || 'The lookup did not come back.');
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let done = null;
            let text = '';
            for (;;) {
                const chunk = await reader.read();
                if (chunk.done) break;
                buffer += decoder.decode(chunk.value, { stream: true });
                // Events are separated by a blank line; a partial one stays
                // in the buffer until the rest of it arrives.
                const parts = buffer.split('\n\n');
                buffer = parts.pop();
                for (const part of parts) {
                    const line = part.split('\n').find((l) => l.startsWith('data: '));
                    if (!line) continue;
                    let item;
                    try { item = JSON.parse(line.slice(6)); } catch { continue; }
                    if (item.error) throw new Error(item.error);
                    if (item.step) setSteps((p) => [...p, item.step]);
                    if (item.text) {
                        text += item.text;
                        setStreamed(text);
                    }
                    if (item.done) done = item.done;
                }
            }

            const data = done || { answer: text, queries: [] };
            setTurns((prev) => [...prev, {
                role: 'assistant',
                text: data.answer,
                queries: data.queries || [],
                attached: data.attached,
                column: data.column_used,
                failed: data.failed,
                people: data.people || [],
                draft: data.draft || null,
            }]);
            if (data.you) setFirstName(data.you);
            if (data.chat_id && data.chat_id !== chatId) setChatId(data.chat_id);
            loadHistory();
            setFile(null);
            if (fileRef.current) fileRef.current.value = '';
        } catch (e) {
            if (e.name === 'AbortError' || axios.isCancel?.(e)
                || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
                setInput(question);
                setTurns((prev) => prev.slice(0, -1));
                setStopped(true);
                return;
            }
            setError(e.message || 'The lookup did not come back. Try again.');
            setInput(question);
            setTurns((prev) => prev.slice(0, -1));
        } finally {
            abortRef.current = null;
            setSteps([]);
            setStreamed('');
            setBusy(false);
        }
    };

    return (
        <div className="nc-layout">
            <aside className="nc-saved">
                <button type="button" className="nc-new" onClick={newChat}>
                    + New chat
                </button>
                <h2 className="nc-saved-head">Recent</h2>
                {saved.length === 0 && (
                    <p className="nc-note">Your past chats appear here.</p>
                )}
                <ul>
                    {saved.map((c) => (
                        <li
                            key={c.id}
                            className={c.id === chatId ? 'on' : ''}
                            onClick={() => openChat(c.id)}
                        >
                            <span>{c.title || 'Untitled'}</span>
                            <button
                                type="button"
                                aria-label={`Delete ${c.title || 'chat'}`}
                                onClick={(e) => removeChat(c.id, e)}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>
        <div className="network-chat">
            <header className="nc-topbar">
                <h1>Ask the network</h1>
            </header>

            <div className="nc-thread">
                {turns.length === 0 && (
                    <div className="nc-empty">
                        <h2>Who do we know?</h2>
                        <p>
                            Ask in your own words, or upload a list of
                            companies. Answers come from the whole team's
                            contacts, and nothing here sends anything.
                        </p>
                        <div className="nc-suggestions">
                            {EXAMPLES.map((e) => (
                                <button type="button" key={e} onClick={() => send(e)}>
                                    {e}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {turns.map((turn, i) => (
                    <div key={i} className={`nc-turn nc-${turn.role}`}>
                        {turn.role === 'user' ? (
                            <p>
                                {turn.text}
                                {turn.file && (
                                    <span className="nc-chip">{turn.file}</span>
                                )}
                            </p>
                        ) : (
                            <>
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {turn.text}
                                </ReactMarkdown>
                                {turn.draft && (
                                    <div className="nc-place">
                                        {placed[i] ? (
                                            <span className="nc-placed">
                                                Saved to your Drafts
                                                {turn.draft.to ? `, addressed to ${turn.draft.to}` : ''}
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                disabled={placing !== null}
                                                onClick={() => placeDraft(i, turn.draft)}
                                            >
                                                {placing === i
                                                    ? 'Saving…'
                                                    : 'Save to my Drafts'}
                                            </button>
                                        )}
                                    </div>
                                )}
                                {turn.people?.length > 0 && (
                                    <div className="nc-actions">
                                        {turn.people.map((p) => {
                                            const a = actionFor(p, firstName);
                                            return (
                                                <button
                                                    type="button"
                                                    key={`${p.name}-${p.firm}`}
                                                    disabled={busy}
                                                    onClick={() => send(a.prompt)}
                                                >
                                                    {a.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {turn.queries?.length > 0 && (
                                    <div className="nc-queries">
                                        <span className="nc-queries-label">
                                            Looked at
                                        </span>
                                        {turn.queries.map((q, j) => (
                                            <span className="nc-query" key={j}>
                                                {describeQuery(q)}
                                            </span>
                                        ))}
                                        {turn.column && (
                                            <span className="nc-note">
                                                read the “{turn.column}” column
                                            </span>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ))}

                {busy && (
                    <div className="nc-turn nc-assistant">
                        {streamed ? (
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                {streamed}
                            </ReactMarkdown>
                        ) : (
                            <div className="nc-steps">
                                {steps.map((q, j) => (
                                    <div className="nc-step" key={j}>
                                        {describeQuery(q)}
                                    </div>
                                ))}
                                <div className="nc-busy">
                                    <span className="nc-dots"><i /><i /><i /></span>
                                    {steps.length ? 'Working' : "Searching the team's contacts"}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div ref={endRef} />
            </div>

            {error && <div className="nc-error">{error}</div>}

            <form
                className="nc-composer"
                onSubmit={(e) => { e.preventDefault(); send(); }}
            >
                {file && (
                    <div className="nc-attached">
                        {file.name}
                        <button
                            type="button"
                            onClick={() => {
                                setFile(null);
                                if (fileRef.current) fileRef.current.value = '';
                            }}
                        >
                            remove
                        </button>
                    </div>
                )}
                <div className="nc-field">
                    <label className="nc-attach" title="Attach a CSV of companies">
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                            <path
                                d="M21.4 11.05 12.25 20.2a5.5 5.5 0 0 1-7.78-7.78l9.2-9.2a3.67 3.67 0 0 1 5.18 5.18l-9.2 9.2a1.83 1.83 0 1 1-2.6-2.6l8.5-8.48"
                                fill="none" stroke="currentColor" strokeWidth="1.7"
                                strokeLinecap="round" strokeLinejoin="round"
                            />
                        </svg>
                    </label>
                    <textarea
                        ref={boxRef}
                        className="nc-input"
                        rows={1}
                        value={input}
                        placeholder={file
                            ? 'Ask about the uploaded list…'
                            : 'Ask about anyone the team knows…'}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={onComposerKey}
                        disabled={busy}
                    />
                    {busy && (
                        <button
                            type="button"
                            aria-label="Stop"
                            className="nc-stop"
                            onClick={stop}
                        >
                            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                                <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                            </svg>
                            {/* The word, not the square alone. A grey circle
                                where the send arrow was is the control four
                                people never found. */}
                            <span>Stop</span>
                        </button>
                    )}
                    {/* The send arrow keeps its place while an answer runs,
                        disabled, and Stop sits beside it. When it used to be
                        REPLACED by the stop button, the stop and the send
                        were the same 32 pixels: a second press, which is what
                        anybody does when a control looks like it did nothing,
                        landed on a send button that had just been handed the
                        question back, and asked it all over again. */}
                    {/* The word, not the arrow alone (Alexandra,
                        2026-09-17). Everything else on this page names what
                        it does, and the deal chat panel has always said
                        Send. */}
                    <button
                        type="submit"
                        aria-label="Send"
                        disabled={busy || !input.trim()}
                    >
                        Send
                    </button>
                </div>
                <p className="nc-hint">
                    {stopped
                        ? 'Stopped. Your question is back in the box.'
                        : 'Return starts a new line. Press Send, or \u2318 + return.'}
                </p>
            </form>
        </div>
        </div>
    );
}

export default NetworkChat;
